import preferences from 'Preferences';
import {BenchlingAction} from 'redux-stores/Action';
import {BenchlingState} from 'redux-stores/reduceApp';
import {ScrollableTablePreferences} from 'ui-library/scrollable/ScrollableTableConstants';

import {ScrollableTableUIActionType} from './ScrollableTableUIReduxActions';

// Save as a constant so we don't incur re-renders unnecessarily.
const NO_HIDDEN_COLUMNS: Array<string> = [];
const NO_COLUMN_ORDER: Array<string> = [];

export class ScrollableTableUISelector {
  constructor(readonly state: BenchlingState) {
    this.state = state;
  }

  getHiddenColumnKeysForTable(
    tableKey: string,
    defaultHiddenColumnKeys?: Array<string> | null,
    // When `tableKey` has no stored value, fall back to this key's preference before defaults. Used
    // for per-section tables so a section inherits the previously-shared setting until customized.
    fallbackTableKey?: string | null
  ): Array<string> {
    const tableState = this.state.ui.scrollableTables.stateByTableKey[tableKey];
    if (tableState && tableState.hiddenColumnKeys != null) {
      return tableState.hiddenColumnKeys;
    }
    const withDefaults = (hiddenColumns: Array<string>): Array<string> =>
      defaultHiddenColumnKeys != null ? hiddenColumns.concat(defaultHiddenColumnKeys) : hiddenColumns;
    const hiddenColumnsPreference = preferences.get(
      `${tableKey}${ScrollableTablePreferences.HIDDEN_COLUMN_NAMESPACE}`
    );
    if (hiddenColumnsPreference) {
      return withDefaults(hiddenColumnsPreference);
    }
    if (fallbackTableKey != null) {
      const fallbackPreference = preferences.get(
        `${fallbackTableKey}${ScrollableTablePreferences.HIDDEN_COLUMN_NAMESPACE}`
      );
      if (fallbackPreference) {
        return withDefaults(fallbackPreference);
      }
    }
    return defaultHiddenColumnKeys ?? NO_HIDDEN_COLUMNS;
  }

  getColumnOrderForTable(tableKey: string, fallbackTableKey?: string | null): Array<string> {
    const tableState = this.state.ui.scrollableTables.stateByTableKey[tableKey];
    if (tableState && tableState.columnOrderKeys != null) {
      return tableState.columnOrderKeys;
    }
    const columnOrderPreference = preferences.get(
      `${tableKey}${ScrollableTablePreferences.COLUMN_ORDER_NAMESPACE}`
    );
    if (columnOrderPreference) {
      return columnOrderPreference;
    }
    if (fallbackTableKey != null) {
      const fallbackPreference = preferences.get(
        `${fallbackTableKey}${ScrollableTablePreferences.COLUMN_ORDER_NAMESPACE}`
      );
      if (fallbackPreference) {
        return fallbackPreference;
      }
    }
    return NO_COLUMN_ORDER;
  }
}

export interface ScrollableTableUIState {
  stateByTableKey: {
    [tableKey: string]: {
      hiddenColumnKeys?: Array<string>;
      columnOrderKeys?: Array<string>;
    };
  };
}

const INITIAL_STATE = {
  stateByTableKey: {},
};

export default function reduceScrollableTableUI(
  state: ScrollableTableUIState = INITIAL_STATE,
  action: BenchlingAction
): ScrollableTableUIState {
  switch (action.type) {
    case ScrollableTableUIActionType.HIDDEN_COLUMNS_UPDATED: {
      const {tableKey, hiddenColumnKeys} = action;
      return {
        ...state,
        stateByTableKey: {
          ...state.stateByTableKey,
          [tableKey]: {...state.stateByTableKey[tableKey], hiddenColumnKeys},
        },
      };
    }
    case ScrollableTableUIActionType.COLUMN_ORDER_UPDATED: {
      const {tableKey, columnOrderKeys} = action;
      return {
        ...state,
        stateByTableKey: {
          ...state.stateByTableKey,
          [tableKey]: {...state.stateByTableKey[tableKey], columnOrderKeys},
        },
      };
    }
    default: {
      return state;
    }
  }
}
